import React, { useRef, useState, useEffect } from "react";
import ActionBar from "../../common/ActionBar";
import { Padding } from "../MyParties";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import MeetingService from "../../../services/MeetingService";
import CustomTable from "../../common/CustomTable/CustomTable";
import moment from "moment";
import FormField from "../../common/FormField/FormField";
import { Formik } from "formik";
import { PrimaryCTAButton } from "../../common/Buttons";
import _ from "lodash";
import { navigate } from "@reach/router";

const filterByStatus = (selectedFilter) => {
  switch (selectedFilter) {
    case "SMS":
      return "SMS";
    case "WhatsApp":
      return "WHATSAPP";
    case "Email":
      return "EMAIL";
    default:
      return "";
  }
};

const initState = [
  { label: "SMS", key: "smsCount" },
  { label: "WhatsApp", key: "whatsappCount" },
  { label: "Email", key: "emailCount" },
];

const extractRows = (res, selectedFilter) => {
  if (selectedFilter === "WhatsApp") {
    return res?.data?.data?.data || res?.data?.data || [];
  }
  return res?.data?.data?.data || res?.data?.data || [];
};

const MAX_DAYS = 3;

const Logs = () => {
  const breadcrumbs = ["Comms Manager", "Logs"];
  const [state, setState] = useState();
  const MTRef = useRef();
  const [filters, setFilters] = useState(initState);
  const { enqueueSnackbar } = useSnackbar();
  const [selectedFilter, setSelectedFilter] = useState();
  const today = moment().format("YYYY-MM-DD");

  const onBreadcrumbClick = (selected) => {
    switch (_.snakeCase(selected)) {
      case "comms_manager":
        navigate("/dashboard/case-manager/comms-manager");
        break;
      default:
        break;
    }
  };

  const [dateRange, setDateRange] = useState({
    startDate: today,
    endDate: today,
  });

  const refreshTable = () => {
    if (MTRef?.current?.onQueryChange) {
      MTRef.current.onQueryChange({
        ...MTRef.current.state.query,
        page: 0,
      });
    }
  };

  useEffect(() => {
    if (!selectedFilter) return;
    if (!dateRange.startDate || !dateRange.endDate) return;
    refreshTable();
  }, [selectedFilter, dateRange.startDate, dateRange.endDate]);

  const data = (query) =>
    new Promise((resolve) => {
      const channel = filterByStatus(selectedFilter);

      if (!channel || !dateRange.startDate || !dateRange.endDate) {
        resolve({ data: [], page: 0, total: 0 });
        return;
      }

      const start = moment(dateRange.startDate).format("YYYY-MM-DD");
      const end = moment(dateRange.endDate).format("YYYY-MM-DD");

      MeetingService.getCommunicationLogs(
        `?channel=${channel}&startDate=${start}&endDate=${end}`,
      )
        .then((res) => {
          let apiData = extractRows(res, selectedFilter);

          apiData = apiData.map((item) => {
            if (selectedFilter === "WhatsApp") {
              return {
                requestDate: item.sentAt || null,
                status: item.status,
                deliveryDate: item.deliveredAt
                  ? moment(item.deliveredAt).format("YYYY-MM-DD")
                  : null,
                deliveryTime: item.deliveredAt
                  ? moment(item.deliveredAt).format("HH:mm:ss")
                  : null,
                telNum: item.mobile,
                isRead: item.isRead,
                sentDateTime: item.sentAt,
              };
            }

            return {
              requestDate: item.requestDate,
              status: item.status,
              deliveryDate: item.deliveryDate,
              deliveryTime: item.deliveryTime,
              telNum: item.telNum,
              sentDateTime: item.sentDateTime,
            };
          });

          const pageSize = query.pageSize || 10;
          const currentPage = query.page || 0;
          const total = apiData.length;
          const lastPage = Math.ceil(total / pageSize);

          const paginatedData = apiData.slice(
            currentPage * pageSize,
            (currentPage + 1) * pageSize,
          );

          setState({
            data: paginatedData,
            lastPage,
            page: currentPage + 1,
            perPage: pageSize,
            total,
          });

          resolve({
            data: paginatedData,
            page: currentPage,
            total,
          });
        })
        .catch((error) => {
          enqueueSnackbar(getErrorMessage(error), { variant: "error" });
          resolve({ data: [], page: 0, total: 0 });
        });
    });

  const columns = [
    {
      field: "requestDate",
      title: "Request Date",
      render: (row) =>
        row.requestDate
          ? moment(row.requestDate).format("DD/MM/YYYY HH:mm")
          : "-",
    },
    {
      field: "deliveryDate",
      title: "Delivery Date",
      render: (row) =>
        row.deliveryDate
          ? `${moment(row.deliveryDate).format("DD/MM/YYYY")} ${row.deliveryTime || ""}`
          : "-",
    },
    {
      field: "status",
      title: "Status",
    },
    {
      field: "telNum",
      title: "Mobile / Email",
      render: (row) => row.telNum || row.mobile || "-",
    },
    {
      field: "sentDateTime",
      title: "Sent Time",
      render: (row) => {
        const val = row.sentDateTime || row.sentAt;
        return val ? moment(val).format("DD/MM/YYYY HH:mm") : "-";
      },
    },
    {
      field: "isRead",
      title: "Read",
      render: (row) => {
        if (selectedFilter !== "WhatsApp") return "-";
        return row.isRead ? "Yes" : "No";
      },
    },
  ];

  const validateDateRange = (startDate, endDate) => {
    if (!startDate || !endDate)
      return "Please select both From Date and To Date.";
    const diff = moment(endDate).diff(moment(startDate), "days");
    if (diff < 0) return "To Date must be after From Date.";
    if (diff > MAX_DAYS)
      return `Duration exceeds allowed limit of ${MAX_DAYS} days.`;
    return null;
  };

  return (
    <div>
      <ActionBar {...{ breadcrumbs, onBreadcrumbClick }} />
      <Padding>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "8px",
          }}
        >
          <Formik
            initialValues={{
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
            }}
            enableReinitialize
            onSubmit={(values) => {
              const error = validateDateRange(values.startDate, values.endDate);
              if (error) {
                enqueueSnackbar(error, { variant: "error" });
                return;
              }

              setDateRange({
                startDate: values.startDate,
                endDate: values.endDate,
              });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleSubmit,
              setFieldValue,
            }) => (
              <form
                onSubmit={handleSubmit}
                style={{ display: "flex", gap: "10px", alignItems: "center" }}
              >
                <FormField
                  input={{
                    name: "startDate",
                    label: "From Date",
                    type: "date",
                  }}
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  onChange={(e) => setFieldValue("startDate", e.target.value)}
                />

                <FormField
                  input={{ name: "endDate", label: "To Date", type: "date" }}
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  onChange={(e) => setFieldValue("endDate", e.target.value)}
                />

                <PrimaryCTAButton
                  style={{
                    margin: 0,
                    height: 30,
                    width: "auto",
                    borderRadius: 10,
                    marginLeft: 28,
                    top: 15,
                  }}
                  type="submit"
                >
                  Apply
                </PrimaryCTAButton>
              </form>
            )}
          </Formik>
        </div>

        <CustomTable
          hidePagination={state?.lastPage <= 1}
          pageSize={state?.data?.length ? state.data.length : 10}
          pluralTitle="Logs"
          placeholderText="Search Logs"
          singularTitle=""
          {...{
            columns,
            data,
            filters,
            setFilters,
            selectedFilter,
            setSelectedFilter,
            MTRef,
          }}
          state={state}
        />
      </Padding>
    </div>
  );
};

export default Logs;
